import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ExactLocation } from "~fulfill/locations/helpers/locationReceiverIdentity";
import type { AddVendorDraft } from "~produce/production/materialItems/helpers/addVendorAssignment";

import locationReceiverIdentity from "~fulfill/locations/helpers/locationReceiverIdentity";
import { VENDOR_TARGET_KIND } from "~produce/purchasing/vendors/helpers/vendorReceiverIdentity";

export const MATERIAL_SOURCE_KIND = "materialItem";

export const MATERIAL_ITEMS_PATH = "/produce/production/material-items";

export type MaterialSourceView = "detail" | "items" | "pricing" | "tags";

const MATERIAL_SOURCE_VIEWS: readonly string[] = ["detail", "items", "pricing", "tags"];

export interface MaterialSourceCheckpoint {
	addVendorDraft?: AddVendorDraft;
	materialItemId: string;
	materialItemName: string;
	panelTab?: "inventory";
	searchText: string;
	view: MaterialSourceView;
}

export interface AddVendorRestore {
	draft: AddVendorDraft;
	materialItemId: string;
}

export interface InspectedMaterialItem {
	id: string;
	name: string;
}

export type OpenMaterialVendor = (
	materialItem: InspectedMaterialItem,
	vendorId: string,
	addVendorDraft?: AddVendorDraft
) => void;

export type OpenMaterialLocation = (materialItem: InspectedMaterialItem, location: ExactLocation) => void;

export type MaterialInspect = (checkpoint: MaterialSourceCheckpoint, target: ContinuationTarget) => void;

export const vendorHop = (
	materialItem: InspectedMaterialItem,
	vendorId: string,
	searchText: string,
	view: MaterialSourceView,
	addVendorDraft?: AddVendorDraft
): [MaterialSourceCheckpoint, ContinuationTarget] => [
	{ addVendorDraft, materialItemId: materialItem.id, materialItemName: materialItem.name, searchText, view },
	{ targetId: vendorId, targetKind: VENDOR_TARGET_KIND }
];

export const locationHop = (
	materialItem: InspectedMaterialItem,
	location: ExactLocation,
	searchText: string,
	view: MaterialSourceView
): [MaterialSourceCheckpoint, ContinuationTarget] => [
	{ materialItemId: materialItem.id, materialItemName: materialItem.name, panelTab: "inventory", searchText, view },
	locationReceiverIdentity.exactLocationTarget(location)
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isNumberOrNull = (value: unknown): boolean => value === null || typeof value === "number";

const isPickedVendor = (value: unknown): boolean =>
	value === null || (isRecord(value) && typeof value.id === "string" && typeof value.name === "string");

const isAddVendorDraft = (value: unknown): value is AddVendorDraft =>
	isRecord(value) &&
	isNumberOrNull(value.costPerUom) &&
	(value.effectiveDate === null || typeof value.effectiveDate === "string") &&
	typeof value.isDefault === "boolean" &&
	typeof value.purchaseUom === "string" &&
	isNumberOrNull(value.unitCost) &&
	isNumberOrNull(value.unitsPerUom) &&
	isPickedVendor(value.vendor) &&
	typeof value.vendorItemId === "string";

export const isMaterialSourceCheckpoint = (value: unknown): value is MaterialSourceCheckpoint =>
	isRecord(value) &&
	(value.addVendorDraft === undefined || isAddVendorDraft(value.addVendorDraft)) &&
	typeof value.materialItemId === "string" &&
	typeof value.materialItemName === "string" &&
	(value.panelTab === undefined || value.panelTab === "inventory") &&
	typeof value.searchText === "string" &&
	typeof value.view === "string" &&
	MATERIAL_SOURCE_VIEWS.includes(value.view);

export const addVendorRestoreFrom = (checkpoint: MaterialSourceCheckpoint): AddVendorRestore | null =>
	checkpoint.addVendorDraft ? { draft: checkpoint.addVendorDraft, materialItemId: checkpoint.materialItemId } : null;

export const inventoryTabRestoreFrom = (checkpoint: MaterialSourceCheckpoint): null | string =>
	checkpoint.panelTab === "inventory" ? checkpoint.materialItemId : null;

export const isMaterialItemsRoute = (pathname: string): boolean =>
	pathname === MATERIAL_ITEMS_PATH || pathname.startsWith(`${MATERIAL_ITEMS_PATH}/`);

const materialEditorKey = (materialItemId: string): string => `materialItem:${materialItemId}`;

export default materialEditorKey;
